import { Modal } from '@repo/ui';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAnalytics } from 'contexts/analytics/AnalyticsProvider';
import { getHasModalOpened, setModalOpened } from 'store/modal';

import { FirstLookCarousel } from './FirstLookCarousel';

import { ActionPlan } from './slides/ActionPlan';
import { ActionPlanDietary } from './slides/ActionPlanDietary';
import { ActionPlanLifestyle } from './slides/ActionPlanLifestyle';
import { ActionPlanSupplements } from './slides/ActionPlanSupplements';
import { Conditions } from './slides/Conditions';
import { DontWorry } from './slides/DontWorry';
import { End } from './slides/End';
import { Intro } from './slides/Intro';
import { MicrobiomeBreakdown } from './slides/MicrobiomeBreakdown';
import { MicrobiomeCategories } from './slides/MicrobiomeCategories';
import { MicrobiomeType } from './slides/MicrobiomeType';
import { Mss } from './slides/Mss';
import { Overview } from './slides/Overview';
import { TopThreeIntro } from './slides/TopThreeIntro';
import { TopThreeItem } from './slides/TopThreeItem';

import { useCurrentKitAndTinyAccount } from 'hooks/useCurrentKitAndTinyAccount';
import { useProcessedActionPlanData } from 'views/Results/Summary/FirstLook/hooks/useProcessedActionPlanData';
import { SLIDE_BACKGROUND_COLORS } from './slideBackgroundColors';

import { AgeGroup, getAgeGroup } from '@repo/utils';
import checkIsKitPro from '@repo/utils/checkIsKitPro';
import useFeature from 'services/features/useFeature';

import { useMainFindingsWithRanges } from '../SummaryCards/NoteCard/useMainFindings';
import styles from './FirstLookPopup.module.scss';
import { Maturation } from './slides/Maturation';
import { ProMss } from './slides/ProMss';

const FirstLookPopup = () => {
  const { currentKit } = useCurrentKitAndTinyAccount();
  const ageGroup = currentKit?.age_at_sampling
    ? getAgeGroup(currentKit.age_at_sampling)
    : null;
  const isBaby = ageGroup === AgeGroup.BABY;

  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const { currentKitId } = useCurrentKitAndTinyAccount();

  const modalIdentifier = {
    name: 'firstLook',
    props: {},
  };

  const { isFeatureEnabled: showVersion2 } = useFeature('first_look_v2');

  const isPopupDismissed = useSelector(getHasModalOpened(modalIdentifier));
  const { data: mainFindingsData } = useMainFindingsWithRanges(currentKitId);
  const topThreeMetrics = mainFindingsData?.mainFindings || [];
  const actionPlanData = useProcessedActionPlanData();

  const isVisible = !isPopupDismissed;

  const isProKit = checkIsKitPro(currentKit);
  const hasTopThreeMetrics = topThreeMetrics.length > 0;
  const hasActionPlanSteps = actionPlanData.steps.length > 1;
  const hasDietaryRecommendations = actionPlanData.dietaryTotal > 0;
  const hasLifestyleRecommendations = actionPlanData.lifestyleTotal > 0;
  const hasSupplementRecommendations = actionPlanData.supplementsTotal > 0;

  const dismissPopup = () => {
    dispatch(setModalOpened(modalIdentifier));
  };

  React.useEffect(() => {
    if (isVisible) {
      trackEvent('First Look', {
        event_type: 'modal_opened',
        total_slides: slides.length,
      });
    }
  }, []);

  if (!isVisible) {
    return null;
  }

  const slides = [
    {
      id: 'intro',
      component: <Intro />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.intro,
    },
    {
      id: 'overview',
      component: <Overview />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.overview,
    },
    {
      id: 'mss',
      component: <Mss />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.mss,
    },
    ...(showVersion2 && isProKit
      ? [
          {
            id: 'pro-mss',
            component: <ProMss />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.mss,
          },
        ]
      : []),
    {
      id: 'microbiome-type',
      component: <MicrobiomeType />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.gutType,
    },
    {
      id: 'microbiome-breakdown',
      component: <MicrobiomeBreakdown />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.microbiomeBreakdown,
    },
    {
      id: 'microbiome-categories',
      component: <MicrobiomeCategories />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.microbiomeCategories,
    },
    ...(ageGroup &&
    (ageGroup === AgeGroup.BABY || ageGroup === AgeGroup.TODDLER)
      ? [
          {
            id: 'maturation-index',
            component: <Maturation />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.microbiomeCategories,
          },
        ]
      : []),
    ...(hasTopThreeMetrics
      ? [
          {
            id: 'top-three-intro',
            component: <TopThreeIntro count={topThreeMetrics.length} />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.topThreeIntro,
          },
          ...topThreeMetrics.map((metric, index) => ({
            id: `top-three-${metric.gvh?.name || index}`,
            component: (
              <TopThreeItem metric={metric} count={topThreeMetrics.length} />
            ),
            backgroundColor: SLIDE_BACKGROUND_COLORS.topThreeItem,
            tapAreaHeight: 'calc(100% - 80px)',
          })),
        ]
      : []),
    {
      id: 'conditions',
      component: <Conditions />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.conditions,
      tapAreaHeight: 'calc(100% - 80px)',
    },
    ...(!isBaby && hasActionPlanSteps
      ? [
          {
            id: 'dont-worry',
            component: <DontWorry />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.dontWorry,
          },
        ]
      : []),
    ...(hasActionPlanSteps
      ? [
          {
            id: 'action-plan',
            component: <ActionPlan />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.actionPlan,
          },
        ]
      : []),
    ...(hasDietaryRecommendations
      ? [
          {
            id: 'action-plan-dietary',
            component: <ActionPlanDietary />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.actionPlanDietary,
          },
        ]
      : []),
    ...(hasLifestyleRecommendations && hasActionPlanSteps
      ? [
          {
            id: 'action-plan-lifestyle',
            component: <ActionPlanLifestyle />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.actionPlanLifestyle,
          },
        ]
      : []),
    ...(hasSupplementRecommendations && hasActionPlanSteps
      ? [
          {
            id: 'action-plan-supplements',
            component: <ActionPlanSupplements />,
            backgroundColor: SLIDE_BACKGROUND_COLORS.actionPlanSupplements,
          },
        ]
      : []),
    {
      id: 'end',
      component: <End />,
      backgroundColor: SLIDE_BACKGROUND_COLORS.end,
      tapAreaHeight: 'calc(100% - 140px)',
    },
  ];

  return (
    <Modal
      isOpen={true}
      className={styles.container}
      onClose={dismissPopup}
      showFloatingX={false}
      hasBodyPadding={false}
      closeOnBackdropClick={false}
    >
      <FirstLookCarousel slides={slides} onClose={dismissPopup} />
    </Modal>
  );
};

export default FirstLookPopup;
